// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import relayEnvironment from 'shared/gql/relayEnvironment';
import UpdateGLAccountMappingMutation from 'main-app/mutations/UpdateGLAccountMapping';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';

const Wrapper = styled.div`
  padding: 0 24px;
  max-width: 900px;
`;

const Description = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 24px;
`;

const MappingCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const MappingLabel = styled.div`
  flex: 1;
`;

const MappingTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const MappingDescription = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 2px;
`;

const AccountSelect = styled.select`
  width: 300px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

const MAPPING_LABELS = {
  INVOICE_RECEIVABLE: {
    title: 'Invoice → Accounts Receivable',
    description: 'Debit this account when an invoice is created',
  },
  INVOICE_REVENUE: {
    title: 'Invoice → Revenue',
    description: 'Credit this account for invoice revenue',
  },
  PAYMENT_CASH: {
    title: 'Payment → Cash',
    description: 'Debit this account when payment is received',
  },
  PAYMENT_RECEIVABLE: {
    title: 'Payment → Accounts Receivable',
    description: 'Credit this account to reduce A/R on payment',
  },
  SHIPMENT_COGS: {
    title: 'Shipment → Cost of Goods Sold',
    description: 'Debit this account for COGS when items ship',
  },
  SHIPMENT_INVENTORY: {
    title: 'Shipment → Finished Goods Inventory',
    description: 'Credit this account to reduce inventory on shipment',
  },
  JOB_COMPLETE_FINISHED: {
    title: 'Job Completion → Finished Goods',
    description: 'Debit this account when a job is completed',
  },
  JOB_COMPLETE_WIP: {
    title: 'Job Completion → Work in Progress',
    description: 'Credit this account to move WIP to finished goods',
  },
  INVENTORY_ADJ_ASSET: {
    title: 'Inventory Adjustment → Inventory Asset',
    description: 'Debit/Credit for inventory adjustments',
  },
  INVENTORY_ADJ_EXPENSE: {
    title: 'Inventory Adjustment → Expense',
    description: 'Offset account for inventory adjustment expense',
  },
  TAX_PAYABLE: {
    title: 'Tax → Tax Payable',
    description: 'Credit this liability account for tax on invoices',
  },
};

type MappingRowProps = {
  mapping: Object,
  accounts: Array<Object>,
};

const MappingRow = ({ mapping, accounts }: MappingRowProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState(
    mapping.account.id,
  );
  const [saving, setSaving] = useState(false);
  const labels = MAPPING_LABELS[mapping.mappingKey] || {
    title: mapping.mappingKey,
    description: '',
  };

  const handleSave = async () => {
    if (selectedAccountId === mapping.account.id) return;
    setSaving(true);
    try {
      await UpdateGLAccountMappingMutation.commit({
        variables: {
          input: {
            mappingKey: mapping.mappingKey,
            accountId: selectedAccountId,
          },
        },
      });
      Actions.alertNotification(
        i18n.t('Mapping updated successfully.'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(
        i18n.t('Error updating mapping.'),
        'error',
      );
    }
    setSaving(false);
  };

  return (
    <MappingCard>
      <MappingLabel>
        <MappingTitle>{labels.title}</MappingTitle>
        <MappingDescription>{labels.description}</MappingDescription>
      </MappingLabel>
      <AccountSelect
        value={selectedAccountId}
        onChange={e => setSelectedAccountId(e.target.value)}
      >
        {accounts.map(acc => (
          <option key={acc.id} value={acc.id}>
            {acc.accountNumber} - {acc.name}
          </option>
        ))}
      </AccountSelect>
      <Button
        theme="blue"
        size="small"
        onClick={handleSave}
        loading={saving}
        disabled={selectedAccountId === mapping.account.id}
      >
        {i18n.t('Save')}
      </Button>
    </MappingCard>
  );
};

const AccountMappings = () => {
  return (
    <QueryRenderer
      environment={relayEnvironment}
      cacheConfig={{ force: true }}
      query={graphql`
        query AccountMappingsQuery {
          glAccountMappings {
            id
            mappingKey
            account {
              id
              accountNumber
              name
            }
          }
          glAccounts(
            first: 200
            isActive: true
            sortBy: { field: ACCOUNT_NUMBER, direction: ASC }
          ) {
            edges {
              node {
                id
                accountNumber
                name
                accountType
              }
            }
          }
        }
      `}
      variables={{}}
      render={({ props }) => (
        <Page>
          <PageHeaderList title={i18n.t('Account Mappings')} />
          {!props ? (
            <Loader />
          ) : (
            <Wrapper>
              <Description>
                {i18n.t(
                  'Configure which GL accounts are used when automatic journal entries are created. These mappings control the accounts debited and credited for each transaction type.',
                )}
              </Description>
              {props.glAccountMappings.map(mapping => (
                <MappingRow
                  key={mapping.id}
                  mapping={mapping}
                  accounts={props.glAccounts.edges.map(e => e.node)}
                />
              ))}
            </Wrapper>
          )}
        </Page>
      )}
    />
  );
};

export default AccountMappings;
